import { ReactNode } from 'react';
import theme from '../../../theme';
import View from '../view';
import { FelaExtend, ThemedStyleProps } from '../../../types/fela';

const styles = {
  h1: () => ({
    fontSize: '20px',
    fontWeight: 300,
    lineHeight: '27px',
    color: '#4B4B4B',
    padding: '0px',
    margin: 0,
  }),
  statusTitle: ({ theme: { colors } }: ThemedStyleProps) => ({
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: 'Futura',
    lineHeight: '19px',
    color: colors.midDarkGrey,
    margin: 0,
    letterSpacing: '0.04em',
    paddingBottom: 10,
  }),
  statusValue: () => ({
    fontSize: '22px',
    fontWeight: 500,
    fontFamily: 'Futura',
    lineHeight: '24px',
    color: '#4D4D4D',
    margin: 0,
    paddingTop: 10,
  }),
  paperTitle: () => ({
    fontSize: '24px',
    fontFamily: 'Futura',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#4B4B4B',
    padding: '0px',
    margin: 0,
  }),
  reportTitle: () => ({
    fontSize: '24px',
    fontWeight: 400,
    fontFamily: 'Futura',
    lineHeight: '31.88px',
    color: '#000000',
    letterSpacing: 1,
    paddingLeft: '0px',
  }),
  SubTitle: () => ({
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Futura',
    lineHeight: '22px',
    color: theme.colors.darkGrey,
  }),
  Label: () => ({
    fontFamily: "Futura",
    fontSize: "12px",
    fontWeight: 400,
    color: theme.colors.grey
  }),
  TextRegular: () => ({
    fontSize: "16px",
    fontWeight: 400,
    color: theme.colors.darkGrey,
  }),
  TextRegularStrong: {
    fontWeight: 600,
  },
  CardTitle: () => ({
    fontFamily: 'Futura',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '32px',
    lineHeight: '39px',
    color: theme.colors.black,
  }),
  DatePickerTitle: ({ theme: { colors } }: ThemedStyleProps) => ({
    fontSize: '24px',
    fontFamily: 'Futura',
    fontWeight: 300,
    color: colors.midDarkGrey,
  }),
  noBorder: () => ({
    borderBottom: '0 !important',
    paddingBottom: '10%',
  }),
  h3: ({ theme: { colors } }: ThemedStyleProps) => ({
    fontSize: '14px',
    color: colors.darkGrey,
    fontWeight: 400,
    padding: '0px',
    lineHeight: "18px",
  }),
  h4: ({ theme: { colors } }: ThemedStyleProps) => ({
    fontSize: '14px',
    color: colors.text,
    fontWeight: 400,
    padding: '0px',
    margin: 0,
  }),
  h5: ({ theme: { colors } }: ThemedStyleProps) => ({
    fontSize: '10px',
    textTransform: 'uppercase',
    color: colors.grey,
    padding: '0px',
    margin: 0,
  }),
  p: () => ({
    fontSize: '16px',
  }),
  p_divider: () => ({
    fontSize: '14px',
    marginTop: 10,
    lineHeight: 0,
    color: '#E9E9E9',
    textAlign: 'left',
  }),
  p_small: () => ({
    fontSize: '12px',
  }),
  light_p: ({ theme: { colors } }: ThemedStyleProps) => ({
    color: colors.white,
  }),
  highlight_p: ({ theme: { colors } }: ThemedStyleProps) => ({
    color: colors.error,
  }),
  link: ({ theme: { colors } }: ThemedStyleProps) => ({
    transition: 'color 200ms ease-in-out',
    '&:hover': {
      color: colors.primary,
    },
  }),
  titleWrapper: ({ theme: { colors } }: ThemedStyleProps) => ({
    display: 'flex',
    flexDirection: 'row',
    '> div': {
      marginLeft: 4,
      marginTop: 2,
      display: 'flex',
      flexDirection: 'row',
      color: colors.primary,
      cursor: 'pointer',
      '> p': {
        color: colors.primary,
        fontSize: 12,
      },
    },
  }),
  light: ({ theme: { colors } }: ThemedStyleProps) => ({
    color: colors.white,
  }),
  a: () => ({
    fontSize: '14px',
    cursor: 'pointer',
  }),
  large_text: {
    fontSize: '16px',
  },
  small_text: {
    fontSize: '11px',
  },
  noPadding: {
    padding: '0 !important',
    margin: '0 !important',
  },
  strong: {
    fontWeight: 800,
  },
};

type TextExtendProps = {
  extend?: FelaExtend;
  noPadding?: boolean;
};

export const PaperTitle = ({ text, noPadding }: { text: string; noPadding?: boolean }) => (
  <View as="h1" extend={[styles.paperTitle, noPadding && styles.noPadding]}>{text}</View>
);

export const StatusTitle = ({ text }: { text: string }) => (
  <View as="h1" extend={[styles.statusTitle]}>{text}</View>
);

export const StatusValue = ({ text, children }: { text?: string; children?: ReactNode }) => (
  <View as="h1" extend={[styles.statusValue]}>{children ?? text}</View>
);

export const H1 = ({ text, noPadding }: { text: string; noPadding?: boolean }) => (
  <View as="h1" extend={[styles.h1, noPadding && styles.noPadding]}>{text}</View>
);

export const ReportTitle = ({ extend, children, noPadding }: TextExtendProps & { children?: ReactNode }) => (
  <View extend={[styles.reportTitle, extend, noPadding && styles.noPadding]}>{children}</View>
);

export const SubTitle = ({
  extend,
  children,
  title,
  noBorder,
  noPadding,
}: TextExtendProps & { children?: ReactNode; title?: ReactNode; noBorder?: boolean }) => (
  <View extend={[styles.SubTitle, extend, noBorder && styles.noBorder, noPadding && styles.noPadding]}>
    <View style={{ flex: 1 }}>{title}</View>
    {children}
  </View>
);
export const Label = ({
  extend,
  children,
  title,
  noBorder,
  noPadding,
}: TextExtendProps & { children?: ReactNode; title?: ReactNode; noBorder?: boolean }) => (
  <View extend={[styles.Label, extend, noBorder && styles.Label, noPadding && styles.noPadding]}>
    <View style={{ flex: 1 }}>{title}</View>
    {children}
  </View>
);

export const TextRegular = ({
  extend,
  children,
  title,
  noBorder,
  noPadding,
  strong,
}: TextExtendProps & {
  children?: ReactNode;
  title?: ReactNode;
  noBorder?: boolean;
  strong?: boolean;
}) => (
  <View
    extend={[
      styles.TextRegular,
      extend,
      noBorder && styles.TextRegular,
      noPadding && styles.noPadding,
      strong && styles.TextRegularStrong,
    ]}
  >
    <View style={{ flex: 1 }}>{title}</View>
    {children}
  </View>
);

export const CardTitle = ({ extend, noPadding, children }: TextExtendProps & { children?: ReactNode }) => (
  <View extend={[styles.CardTitle, extend, noPadding && styles.noPadding]}>{children}</View>
);

export const DatePickerTitle = ({
  extend,
  children,
  title1,
  title2,
  noBorder,
  noPadding,
}: TextExtendProps & { children?: ReactNode; title1?: ReactNode; title2?: ReactNode; noBorder?: boolean }) => (
  <View extend={[styles.DatePickerTitle, extend, noBorder && styles.noBorder, noPadding && styles.noPadding]}>
    <View style={{ flex: 1 }}>
      {title1}
      <br /> {title2}
    </View>
    {children}
  </View>
);

export const H3 = ({
  children,
  noPadding,
  light,
  linkTitle,
  linkIcon,
  onClickLink,
  color,
  extend,
}: TextExtendProps & {
  children?: ReactNode;
  light?: boolean;
  linkTitle?: string;
  linkIcon?: ReactNode;
  onClickLink?: () => void;
  color?: string;
}) => (
  <View
    as="h3"
    extend={[
      styles.h3,
      noPadding && styles.noPadding,
      light && styles.light,
      linkTitle && styles.titleWrapper,
      color && { color },
      extend,
    ]}
  >
    {children}
    {linkTitle && (
      <View onClick={() => onClickLink?.()}>
        {linkIcon}
        <P strong noPadding>
          {linkTitle.toUpperCase()}
        </P>
      </View>
    )}
  </View>
);

export const H4 = ({ children, extend }: TextExtendProps & { children?: ReactNode }) => (
  <View as="h4" extend={[styles.h4, extend]}>
    {children}
  </View>
);

export const H5 = ({ noPadding, children, extend }: TextExtendProps & { children?: ReactNode }) => (
  <View as="h5" extend={[styles.h5, noPadding && styles.noPadding, extend]}>
    {children}
  </View>
);

export const P = ({
  children,
  extend,
  large,
  light,
  strong,
  style,
  link,
  noPadding,
  highlight,
  divider,
  small,
}: TextExtendProps & {
  children?: ReactNode;
  large?: boolean;
  light?: boolean;
  strong?: boolean;
  style?: FelaExtend;
  link?: boolean;
  highlight?: boolean;
  divider?: boolean;
  small?: boolean;
}) => (
  <View
    as="p"
    extend={[
      small && styles.p_small,
      divider && styles.p_divider,
      styles.p,
      light && styles.light_p,
      large && styles.large_text,
      link && styles.link,
      highlight && styles.highlight_p,
      strong && styles.strong,
      noPadding && styles.noPadding,
      style,
      extend,
    ]}
    children={children}
  />
);

export const A = ({
  text,
  children,
  large,
  small,
  extend,
  ...props
}: TextExtendProps & {
  text?: string;
  children?: ReactNode;
  large?: boolean;
  small?: boolean;
} & React.ComponentPropsWithoutRef<'a'>) => (
  <View as="a" {...props} extend={[styles.a, extend, large && styles.large_text, small && styles.small_text]}>
    {text || children}
  </View>
);

export const Span = ({
  extend,
  children,
  fontFamily,
  ...props
}: TextExtendProps & {
  children?: ReactNode;
  fontFamily?: FelaExtend;
} & React.ComponentPropsWithoutRef<'span'>) => (
  <View as="span" {...props} extend={[extend, fontFamily]}>
    {children}
  </View>
);
